/**
 * The members a sponsor claims as its own, wherever they are being cared for.
 *
 * SEPARATE FROM "What we funded" ON PURPOSE. A cohort is a membership claim —
 * Ohai enrolled her, a health plan rostered her — and a grantor making one would
 * be asserting something false. The two screens answer different questions and a
 * single organisation may legitimately see only one of them.
 *
 * THE POINT IS THE ORGANISATION COLUMN. A cohort that sat inside one
 * organisation would tell a sponsor nothing a roster could not. What makes this
 * worth a screen is that the mother is in THIS sponsor's cohort while being
 * served by SOMEBODY ELSE'S doula, so that column is given prominence rather
 * than tucked away at the end.
 *
 * WHAT IS ABSENT IS THE DESIGN. No date of birth, no address, no diagnosis, no
 * note, no message content. §3.15 says a partner sees activity and disbursement
 * status for its own cohort "without reaching her broader record", and the
 * server enforces that in the SELECT list. This screen does not try to show more
 * than it is given.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { authFetchJson } from '@/lib/authFetch';
import { PageContainer, PageHeader, PageState } from '@/components/weavein';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, Users } from 'lucide-react';

interface CohortMember {
  member_id: number;
  name: string;
  preferred_language: string | null;
  joined_via: string;
  identifier_value: string | null;
  in_cohort_since: string | null;
  served_by_org: string | null;
  served_by_org_id: number | null;
  doula: string | null;
  assigned_since: string | null;
  visits_logged: number;
  visits_remaining: number | null;
  opt_in: string | null;
  paid_total: number | null;
  pending_total: number | null;
}

interface CohortView {
  sponsor_id: number;
  sponsor_name: string;
  members: CohortMember[];
  organisations_serving: number;
  message: string;
}

function money(v: number | null): string {
  if (v === null || v === undefined) return '—';
  return `$${Number(v).toFixed(2)}`;
}

function when(v: string | null): string {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, { dateStyle: 'medium' });
}

export default function SponsorCohort() {
  const { t } = useTranslation();
  const [data, setData] = useState<CohortView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await authFetchJson<CohortView>('/api/sponsor/v1/cohort'));
    } catch (e) {
      setError(e instanceof Error ? e.message : t('sponsorCohort.loadFailed', 'We could not load this cohort.'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <PageState variant="loading" />;

  if (error) {
    return (
      <PageContainer>
        <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title={t('sponsorCohort.title', 'Our cohort')}
        description={t(
          'sponsorCohort.description',
          'The members we brought in, and how their care is going wherever it is being delivered.',
        )}
      />

      {data && data.organisations_serving > 0 && (
        <p className="mb-4 text-sm text-muted-foreground">
          {t('sponsorCohort.across', 'Cared for across {{count}} organisations', {
            count: data.organisations_serving,
          })}
        </p>
      )}

      {!data || data.members.length === 0 ? (
        <div className="rounded-md border p-8 text-center">
          <Users className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-3 text-sm font-medium">
            {t('sponsorCohort.empty', 'Nobody is in this cohort yet.')}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">{data?.message}</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/40 text-left">
              <tr>
                <th className="p-3 font-medium">{t('sponsorCohort.member', 'Member')}</th>
                <th className="p-3 font-medium">{t('sponsorCohort.servedBy', 'Cared for by')}</th>
                <th className="p-3 font-medium">{t('sponsorCohort.visits', 'Visits')}</th>
                <th className="p-3 text-right font-medium">{t('sponsorCohort.paid', 'Paid')}</th>
                <th className="p-3 text-right font-medium">{t('sponsorCohort.pending', 'Pending')}</th>
                <th className="p-3 font-medium">{t('sponsorCohort.since', 'In cohort since')}</th>
              </tr>
            </thead>
            <tbody>
              {data.members.map((m) => (
                <tr key={m.member_id} className="border-b last:border-0">
                  <td className="p-3">
                    <span className="font-medium">{m.name}</span>
                    {m.preferred_language && (
                      <span className="mt-0.5 block text-xs text-muted-foreground">
                        {m.preferred_language}
                      </span>
                    )}
                  </td>
                  <td className="p-3">
                    {/* Given prominence: a mother in our cohort served by another
                        organisation's doula is the whole reason for this screen. */}
                    <span className="font-medium">{m.served_by_org || '—'}</span>
                    {m.doula && (
                      <span className="mt-0.5 block text-xs text-muted-foreground">{m.doula}</span>
                    )}
                  </td>
                  <td className="p-3 text-muted-foreground">
                    {t('sponsorCohort.visitsLogged', '{{count}} logged', { count: m.visits_logged })}
                    {m.visits_remaining !== null && (
                      <Badge variant={m.visits_remaining === 0 ? 'danger' : 'secondary'} className="ml-2">
                        {t('sponsorCohort.visitsLeft', '{{count}} left', { count: m.visits_remaining })}
                      </Badge>
                    )}
                  </td>
                  <td className="p-3 text-right tabular-nums">{money(m.paid_total)}</td>
                  <td className="p-3 text-right tabular-nums">{money(m.pending_total)}</td>
                  <td className="p-3 text-muted-foreground">{when(m.in_cohort_since)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data && data.members.length > 0 && (
        <p className="mt-4 text-sm text-muted-foreground">{data.message}</p>
      )}
    </PageContainer>
  );
}
